import { useState } from "react";
import { Ticket, X } from "lucide-react";
import { useTechs } from "@/context/TechContext";

export const TicketFormModal = ({ device, subscriber, onClose, onSubmit, editTicket = null }) => {
  const isEditMode = !!editTicket;
  const { techs } = useTechs();
  
  const [formData, setFormData] = useState(
    isEditMode 
      ? {
          ticketId: editTicket.ticketId,
          subscriberName: editTicket.subscriberName,
          deviceId: editTicket.deviceId,
          deviceType: editTicket.deviceType,
          issueType: editTicket.issueType,
          location: editTicket.location,
          model: editTicket.model || '',
          lastSeen: editTicket.lastSeen || '',
          priority: editTicket.priority,
          assignedTo: editTicket.assignedTo,
          dueDate: editTicket.dueDate,
          description: editTicket.description,
          notes: editTicket.notes || ''
        }
      : {
          ticketId: 'auto',
          subscriberName: subscriber?.name || '',
          deviceId: device?.id || '',
          deviceType: device?.type || '',
          issueType: device?.status || '',
          location: device?.location || '',
          model: device?.model || '',
          lastSeen: device?.lastSeen || '',
          priority: 'Medium',
          assignedTo: '',
          dueDate: '',
          description: `Device ${device?.id} has not been seen for ${device?.lastSeen}. Last contact: ${device?.lastSeenDate}. Requires immediate attention.`,
          notes: ''
        }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="modal-overlay" data-testid="ticket-modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="modal-title">
              <Ticket size={20} />
              {isEditMode ? 'Edit Service Ticket' : 'Create Service Ticket'}
            </h2>
            <p className="modal-subtitle">{isEditMode ? `Ticket ID: ${formData.ticketId}` : 'New Service Ticket'}</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} data-testid="modal-close-btn">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ticket-form">
          <div className="form-section">
            <h3 className="form-section-title">Subscriber Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Subscriber Name</label>
                <input 
                  type="text" 
                  name="subscriberName" 
                  value={formData.subscriberName} 
                  onChange={handleChange}
                  readOnly
                  className="form-input readonly"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Device Information</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Device ID</label>
                <input 
                  type="text" 
                  name="deviceId" 
                  value={formData.deviceId} 
                  onChange={handleChange}
                  readOnly
                  className="form-input readonly"
                />
              </div>
              <div className="form-group">
                <label>Device Type</label>
                <input 
                  type="text" 
                  name="deviceType" 
                  value={formData.deviceType} 
                  onChange={handleChange}
                  readOnly
                  className="form-input readonly"
                />
              </div>
              <div className="form-group">
                <label>Issue Type</label>
                <input 
                  type="text" 
                  name="issueType" 
                  value={formData.issueType} 
                  onChange={handleChange}
                  readOnly
                  className="form-input readonly issue-type"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Location</label>
                <input 
                  type="text" 
                  name="location" 
                  value={formData.location} 
                  onChange={handleChange}
                  readOnly
                  className="form-input readonly"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Ticket Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Priority</label>
                <select 
                  name="priority" 
                  value={formData.priority} 
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
              <div className="form-group">
                <label>Assigned To</label>
                <select 
                  name="assignedTo" 
                  value={formData.assignedTo} 
                  onChange={handleChange}
                  className="form-select"
                  data-testid="assigned-to-select"
                >
                  <option value="">-- Select --</option>
                  {techs.map(tech => (
                    <option key={tech.id} value={`${tech.firstName} ${tech.lastName}`}>
                      {tech.firstName} {tech.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input 
                  type="date" 
                  name="dueDate" 
                  value={formData.dueDate} 
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
            {isEditMode && (
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select 
                    name="status" 
                    value={formData.status || 'Open'} 
                    onChange={handleChange}
                    className="form-select"
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Pending">Pending</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group full-width">
                <label>Description</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleChange}
                  className="form-textarea"
                  rows={3}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Additional Notes</label>
                <textarea 
                  name="notes" 
                  value={formData.notes} 
                  onChange={handleChange}
                  className="form-textarea"
                  rows={2}
                  placeholder="Add any additional notes or instructions..."
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" data-testid="submit-ticket-btn">
              <Ticket size={16} />
              {isEditMode ? 'Save Changes' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
